<template>
  <div class="flex flex-col gap-y-6" @click="closeAllFilters">
    <HandoverFilterBar @viewHistory="handleViewHistory" />

    <div class="flex flex-col min-h-[300px]">
      <template v-if="isLoading">
        <div class="w-full overflow-x-auto border border-[#d8d8e2] rounded-lg">
          <table class="w-full min-w-[1180px] table-fixed border-collapse">
            <thead>
              <tr class="bg-[#f0f0f4]">
                <th
                  v-for="col in skeletonColumns"
                  :key="col.label"
                  class="px-3 py-2.5 text-[13px] font-bold text-[#272735] text-center leading-tight align-middle"
                  :class="col.width"
                >
                  {{ col.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="n in 5" :key="n" class="border-t border-[#e8e8ed] bg-white animate-pulse">
                <td class="px-3 py-3">
                  <div class="flex items-center gap-3">
                    <div class="shrink-0 w-10 h-10 rounded-full bg-slate-200" />
                    <div class="flex flex-col gap-1">
                      <div class="h-3.5 w-28 bg-slate-200 rounded" />
                      <div class="h-3 w-16 bg-slate-200 rounded" />
                    </div>
                  </div>
                </td>
                <td class="px-3 py-3">
                  <div class="h-3.5 w-24 bg-slate-200 rounded" />
                </td>
                <td class="px-3 py-3">
                  <div class="h-3.5 w-32 bg-slate-200 rounded" />
                </td>
                <td class="px-3 py-3">
                  <div class="flex justify-center">
                    <div class="h-3.5 w-6 bg-slate-200 rounded" />
                  </div>
                </td>
                <td class="px-3 py-3">
                  <div class="flex justify-center">
                    <div class="h-3.5 w-20 bg-slate-200 rounded" />
                  </div>
                </td>
                <td class="px-3 py-3">
                  <div class="flex justify-center">
                    <div class="h-3.5 w-6 bg-slate-200 rounded" />
                  </div>
                </td>
                <td class="px-3 py-3">
                  <div class="flex justify-center">
                    <div class="h-6 w-20 bg-slate-200 rounded-full" />
                  </div>
                </td>
                <td class="px-3 py-3">
                  <div class="flex justify-center">
                    <div class="h-8 w-20 bg-slate-200 rounded" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <template v-else>
        <HandoverApprovalsTable
          v-if="pendingApprovals.length > 0"
          :items="pendingApprovals"
          @evaluate="handleEvaluate"
          @view-transition-details="handleViewTransitionDetails"
          @view-details="handleViewDetails"
        />
        <EmptyList v-else title="Nenhuma aprovação pendente" />
      </template>
    </div>

    <v-pagination
      v-if="!isLoading && pendingApprovals.length > 0"
      v-model="currentPage"
      :total-visible="5"
      :length="meta.totalPages"
      density="comfortable"
      color="primary"
      data-testid="handover-approvals-pagination"
      @update:model-value="changePage"
    />

    <HandoverHistoryModal v-model="showHistoryModal" />

    <HandoverHistoryDetailModal v-model="showDetailModal" :item="detailItem" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useHandoverStore } from '@/pinia-store/useHandoverStore'
import type { AdminQueueResponse } from '@/Network/Types/Responses/HandoverInviteResponses'
import HandoverFilterBar from './components/HandoverFilterBar.vue'
import HandoverApprovalsTable from './components/HandoverApprovalsTable.vue'
import EmptyList from '@/components/EmptyList.vue'
import HandoverHistoryModal from './components/HandoverHistoryModal.vue'
import HandoverHistoryDetailModal from './components/HandoverHistoryDetailModal.vue'

// Espelha as colunas de HandoverApprovalsTable.vue para o skeleton não "pular" ao carregar
const skeletonColumns = [
  { label: 'Profissional que está passando', width: 'w-[230px]' },
  { label: 'Escala', width: 'w-[160px]' },
  { label: 'Unidade', width: 'w-[170px]' },
  { label: 'Profissionais que querem assumir', width: 'w-[150px]' },
  { label: 'Data do convite', width: 'w-[110px]' },
  { label: 'Plantões', width: 'w-[90px]' },
  { label: 'Expiração', width: 'w-[140px]' },
  { label: 'Ações', width: 'w-[130px]' }
]

const router = useRouter()
const handoverStore = useHandoverStore()
const { pendingApprovals, isLoading, meta } = storeToRefs(handoverStore)

const currentPage = ref(1)
const showHistoryModal = ref(false)
const showDetailModal = ref(false)
const detailItem = ref<AdminQueueResponse | null>(null)

// A linha já traz o pacote completo: guardá-lo no store deixa a página de
// detalhes abrir sem nova requisição (ela só busca quando o pacote não bate).
const handleViewTransitionDetails = (item: AdminQueueResponse) => {
  handoverStore.setCurrentEvaluation(item)
  router.push({
    name: 'HandoverDetails',
    params: { packageId: String(item.package.id) }
  })
}

// A linha da listagem já carrega tudo que o modal de detalhes mostra — reusamos o
// mesmo modal do histórico em vez de duplicar a tela ou refazer a requisição.
const handleViewDetails = (item: AdminQueueResponse) => {
  detailItem.value = item
  showDetailModal.value = true
}

const changePage = (page: number) => {
  currentPage.value = page
  handoverStore.fetchPendingApprovals(page)
}

const handleEvaluate = (item: AdminQueueResponse) => {
  handoverStore.setCurrentEvaluation(item)
  router.push({
    name: 'HandoverEvaluation',
    params: { packageId: String(item.package.id) }
  })
}

const handleViewHistory = () => {
  showHistoryModal.value = true
}

const closeAllFilters = () => {
  // Propaga click para fechar dropdowns de filtros
}

watch(
  () => meta.value.page,
  (newPage) => {
    currentPage.value = newPage
  }
)

onMounted(() => {
  handoverStore.fetchPendingApprovals(1)
})
</script>
